<template>
    <div class="metric-entry-modal">
        <b-modal body-class="light-bg"
                 size="lg"
                 :id="modalId"
                 @shown="shown"
                 @ok="ok"
                 @cancel="clear">
            <template v-slot:modal-header>
                <h5>{{ title }}</h5>
            </template>

            <div v-if="stackElements" class="d-flex flex-row justify-content-center">
                <b-calendar
                    :date-info-fn="highlightedDates"
                    v-model="selectedDate"></b-calendar>
            </div>

            <b-row class="d-flex flex-row justify-content-center">
                <b-col v-if="!stackElements" class="m-2">
                    <b-calendar
                        :date-info-fn="highlightedDates"
                        v-model="selectedDate"></b-calendar>
                </b-col>
                <b-col>
                    <b-row class="m-3">
                        <b-form-radio-group
                            buttons
                            class="buttons-value"
                            v-model="selectedMetric"
                            :options="radioLabels"
                            :stacked="true">
                        </b-form-radio-group>
                    </b-row>
                    <b-row class="justify-content-center">
                        <b-form
                            @submit.prevent
                            class="m-3"
                            v-if="selectedMetric != ''"
                            inline
                            :label="metricLabels[selectedMetric]">

                            <div v-if="selectedMetric == 'BLOOD_PRESSURE'">
                                <b-form-group
                                    :label="descriptionLabel">
                                    <b-input-group class=""
                                                   :append="unitLabels[selectedMetric]">
                                        <b-form-input type="number"
                                                      :value="getReadingValue('SYSTOLIC')"
                                                      @change="(value) => setReadingValue('SYSTOLIC', value)"
                                        ></b-form-input>
                                        <b-form-input type="number"
                                                      :value="getReadingValue('DIASTOLIC')"
                                                      @change="(value) => setReadingValue('DIASTOLIC', value)"
                                        ></b-form-input>
                                    </b-input-group>
                                </b-form-group>
                            </div>
                            <div v-else>
                                <b-form-group
                                    :label="descriptionLabel">
                                    <b-input-group
                                        :append="unitLabels[selectedMetric]">
                                        <b-form-input type="number"
                                                      :value="getReadingValue(selectedMetric)"
                                                      @change="(value) => setReadingValue(selectedMetric, value)"></b-form-input>
                                    </b-input-group>
                                </b-form-group>
                            </div>
                        </b-form>
                        <b-alert :show="bloodPressureError" variant="danger">{{
                                $i18n.gt('LABEL_ENTER_SYSTOLIC_DIASTOLIC')
                            }}
                        </b-alert>
                    </b-row>
                </b-col>
            </b-row>
        </b-modal>
    </div>

</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue'
import MetricUtil from "@/providers/Metrics"
import moment from 'moment'
import * as _ from 'lodash'

export default Vue.extend({
    name: "MetricEntryModal",
    props: {
        modalId: String,
        title: String,
        metrics: Array,
        service: String,
        onSubmit: Function, //Requires a parameter to pass entries
    },
    data() {
        return {
            selectedMetric: "WEIGHT",
            selectedDate: "",
            entries: {},
            entryDates: [],
            bloodPressureError: false,
            metricUtil: MetricUtil,
            // metricLabels: MetricUtil.metricLabels(),
            unitLabels: MetricUtil.unitLabels()
        }
    },
    computed: {
        metricLabels() {
          return MetricUtil.metricLabels()
        },

        radioLabels() {
            //Find the value for each metric on the selected date and display it on the radio button
            return _.map(this.metricLabels, (value, key) => {

                let readingText
                let hasData
                if (key == 'BLOOD_PRESSURE') {
                    let sysValue = this.getReadingValue('SYSTOLIC')
                    let diaValue = this.getReadingValue('DIASTOLIC')
                    hasData = sysValue && diaValue
                    readingText = hasData ? `${sysValue}/${diaValue}` : ' - '
                } else {
                    let readingValue = this.getReadingValue(key)
                    hasData = !!readingValue
                    readingText = hasData ? `${readingValue} ${this.unitLabels[key]}` : ' - '
                }
                return {text: value + ' ' + readingText, value: key}
            })
        },
        prettyDate() {
            return moment(this.selectedDate).format('MMMM Do')
        },
        descriptionLabel() {
            return this.$i18n.gt('LABEL_METRIC_ENTRY_MODAL_PHRASE')
                .replace('{metric_date}', this.prettyDate)
                .replace('{metric_name}', this.metricLabels[this.selectedMetric].toLowerCase())
        },
        stackElements() {
            return this.$mq == 'sm' || this.$mq == 'md' || this.$mq == 'lg'
        }
    },
    methods: {
        ok(evt) {
            if (this.bloodPressureError) {
                //Prevent the modal from closing
                evt.preventDefault()
            } else {
                this.onSubmit(this.entries)
                this.clear()
            }
        },
        clear() {
            this.selectedDate = ''
            this.selectedMetric = 'WEIGHT'
            this.entries = {}
            this.bloodPressureError = false
        },
        highlightedDates(ymd, date) {
            //table-info is a css class for highlighting the calender dates
            return this.entryDates.includes(ymd) ? 'table-info' : ''
        },
        getReadingValue(key) {
            if (this.entries[key]) {
                return this.entries[key][this.selectedDate]
            } else {
                return null
            }
        },
        setReadingValue(metric, value) {
            if (!this.entries[metric]) {
                this.entries[metric] = {}
            }
            // this.entries[metric][this.selectedDate] = value
            Vue.set(this.entries[metric], this.selectedDate, value)

            //The first clause is to short-circuit if systolic/diastolic aren't defined
            this.bloodPressureError = ((metric == 'SYSTOLIC' || metric == 'DIASTOLIC') &&
                !this.entries['SYSTOLIC'][this.selectedDate] != !this.entries['DIASTOLIC'][this.selectedDate])
        },
        shown() {
            //Put the participants metrics into a format which is easier to work with
            this.entries = MetricUtil.metricsToEntries(this.metrics, this.service)

            //Find all the dates on which there are metrics. This is used to highlight dates on the calendar
            this.entryDates = _.reduce(this.entries, (result, entries) => {
                return result.concat(Object.keys(entries))
            }, [])

            //Set calendar to today
            const now = new Date()
            this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        },
    },
})
</script>
<style lang="scss">

//@import '../../sass/variables_mixins';

//.buttons-value {
//    width: 100%;
//
//    .input-group-append {
//        width: 30%;
//    }
//
//    .input-group-text {
//        width: 100%;
//    }
//
//    button:focus {
//        outline: none;
//    }
//}
//
//.buttons-value__label {
//    font-weight: bold !important;
//    border-top-right-radius: 0 !important;
//    border-bottom-right-radius: 0 !important;
//    width: 70%;
//
//    &.active {
//        background-color: var(--btn-bg-hover) !important;
//        border-color: var(--btn-bg-hover) !important;
//    }
//
//    &:focus, &.focus {
//        outline: none !important;
//        @include box-shadow(none !important);
//    }
//}

//.active {
//    .buttons-value__values, .buttons-value__label {
//        color: #fff;
//    }
//}
//
.inputs-value {
    .form-control {
        color: #222;
    }
}

</style>

<style>

/*.hide-radio-button input[type="radio"] {*/
/*    opacity: 0;*/
/*    position: fixed;*/
/*    width: 0;*/
/*}*/
</style>
